import React, { useState, useEffect } from 'react';
import { Button, DatePicker, Select, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useQuery, gql } from '@apollo/client';
import { allChannelResponse, GET_ALL_CHANNEL } from '../../../../../graphql/queries/getChannelAll';
import { useTranslation } from 'react-i18next';
import ParcelFilterStyle from '../../../../delivery/components/ParcelFilterStyle';
import { getSmartLabelClassName } from '../../../../../utils/labelWidthUtils';
import { useShouldUseVerticalLayout, useFilterLabelClassName } from '../../../../../utils/languageUtils';

const { RangePicker } = DatePicker;

const GET_CARRIER_OPTIONS = gql`
  query GetCarrierOptions($includeSwiftX: Boolean!) {
    getCarrierOptions(includeSwiftX: $includeSwiftX) {
      value
      name
    }
  }
`;

interface WorkProgressFiltersProps {
  onTimeRangeChange: (timeRange: { start: Dayjs; end: Dayjs }) => void;
  onChannelChange: (channelId: string | null) => void;
  onCarrierChange: (carrierCode: number | null) => void;
  onFilter?: () => void;
  onReset?: () => void;
}

const WorkProgressFilters: React.FC<WorkProgressFiltersProps> = ({
  onTimeRangeChange,
  onChannelChange,
  onCarrierChange,
  onFilter,
  onReset,
}) => {
  const { t } = useTranslation();
  const useVerticalLayout = useShouldUseVerticalLayout();
  // 默认时间为24小时
  const getTimeRangeByHours = (hours: number): [Dayjs, Dayjs] => {
    const end = dayjs().minute(0).second(0).millisecond(0);
    const start = end.subtract(hours, 'hour').minute(0).second(0).millisecond(0);
    return [start, end];
  };

  const getDefaultTimeRange = (): [Dayjs, Dayjs] => getTimeRangeByHours(24);
  
  const [timeRangePreset, setTimeRangePreset] = useState<'6h' | '12h' | '24h' | '48h' | 'custom'>('24h');
  const [timeRange, setTimeRange] = useState<[string | null, string | null] | null>(() => {
    const [start, end] = getDefaultTimeRange();
    return [start.format('YYYY-MM-DD HH:mm:ss'), end.format('YYYY-MM-DD HH:mm:ss')];
  });
  const [channelId, setChannelId] = useState<string | null>(null);
  const [carrierCode, setCarrierCode] = useState<number | null>(null);
  const [channelOptions, setChannelOptions] = useState<{ value: string; label: string }[]>([]);
  const [carrierOptions, setCarrierOptions] = useState<{ value: string; label: string }[]>([]);

  const { data } = useQuery<allChannelResponse>(GET_ALL_CHANNEL);
  const { data: carrierData } = useQuery(GET_CARRIER_OPTIONS, {
    variables: { includeSwiftX: true }
  });

  useEffect(() => {
    if (data?.getAllChannels) {
      const channels = Array.isArray(data.getAllChannels) ? data.getAllChannels : [data.getAllChannels];
      const options = channels.map(channel => ({
        value: channel.id,
        label: channel.channelName
      }));
      setChannelOptions(options);
    }
  }, [data]);

  useEffect(() => {
    if (carrierData?.getCarrierOptions) {
      const options = carrierData.getCarrierOptions.map((carrier: any) => ({
        value: carrier.value.toString(),
        label: carrier.name
      }));
      setCarrierOptions(options);
    }
  }, [carrierData]);

  // 初始化时自动设置24小时范围
  useEffect(() => {
    const [start, end] = getDefaultTimeRange();
    onTimeRangeChange({ start, end });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTimeRangePresetChange = (preset: '6h' | '12h' | '24h' | '48h' | 'custom') => {
    setTimeRangePreset(preset);
    if (preset === 'custom') return;
    const hours = { '6h': 6, '12h': 12, '24h': 24, '48h': 48 }[preset];
    const [start, end] = getTimeRangeByHours(hours);
    setTimeRange([start.format('YYYY-MM-DD HH:mm:ss'), end.format('YYYY-MM-DD HH:mm:ss')]);
    onTimeRangeChange({ start, end });
  };

  // 处理时间范围变化
  const handleTimeRangeChange = (dates: [string | null, string | null] | null) => {
    setTimeRange(dates);
    if (dates && dates[0] && dates[1]) {
      setTimeRangePreset('custom');
      const start = dayjs(dates[0]).minute(0).second(0).millisecond(0);
      const end = dayjs(dates[1]).minute(0).second(0).millisecond(0);
      // 自定义时间范围限制：跨度最多 31 天
      const diffInDays = end.startOf('day').diff(start.startOf('day'), 'day');
      if (diffInDays > 31) {
        message.warning(
          t('wms.progress.bigBag.messages.maxRange', '时间范围最多支持 31 天，请重新选择。')
        );
        return;
      }
      onTimeRangeChange({ start, end });
    }
  };

  const handleTimeRangePickerChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      handleTimeRangeChange([dates[0].format('YYYY-MM-DD HH:mm:ss'), dates[1].format('YYYY-MM-DD HH:mm:ss')]);
      return;
    }
    handleTimeRangeChange(null);
  };

  // 处理渠道变化
  const handleChannelChange = (value: string | undefined) => {
    const channelIdValue = value || null;
    setChannelId(channelIdValue);
    onChannelChange(channelIdValue);
  };

  // 处理尾程商变化
  const handleCarrierChange = (value: string | undefined) => {
    const code = value ? Number(value) : null;
    setCarrierCode(code);
    onCarrierChange(code);
  };

  // 处理重置
  const handleReset = () => {
    const [start, end] = getDefaultTimeRange();
    setTimeRangePreset('24h');
    setTimeRange([start.toISOString(), end.toISOString()]);
    setChannelId(null);
    setCarrierCode(null);
    onTimeRangeChange({ start, end });
    onChannelChange(null);
    onCarrierChange(null);
    onReset?.();
  };

  // 第一行三等分布局：最近24小时+范围 / 客户名称 / 尾程商
  const colClientSpanClassName = getSmartLabelClassName([t('wms.progress.filters.clientName')]);
  const colCarrierSpanClassName = getSmartLabelClassName([t('wms.progress.filters.carrier')]);
  const timeRangeLabelClassName = useFilterLabelClassName(colClientSpanClassName);
  const clientNameLabelClassName = useFilterLabelClassName(colClientSpanClassName);
  const carrierLabelClassName = useFilterLabelClassName(colCarrierSpanClassName);

  return (
    <div className="mb-2 pl-0 py-2">
      <div className="flex flex-col lg:flex-row gap-4">
        {/* 筛选条件 - 使用 Grid 布局确保列对齐，第一列稍微宽一点以适应时间范围 */}
        <div className="flex-1">
          {useVerticalLayout ? (
            // 非中文布局：第一行是标题，第二行是输入框；gap-y-2 缩小标签与输入框的间距，避免英文下显得过宽
            <div className="grid gap-x-10 gap-y-1.5" style={{ gridTemplateColumns: '1.3fr 1fr 1fr' }}>
              {/* 第一行：三个标题 */}
              <div className="min-w-0">
                <span className={timeRangeLabelClassName}>{t('wms.progress.filters.timeRange')}</span>
              </div>
              <div className="min-w-0">
                <span className={clientNameLabelClassName}>{t('wms.progress.filters.clientName')}</span>
              </div>
              <div className="min-w-0">
                <span className={carrierLabelClassName}>{t('wms.progress.filters.carrier')}</span>
              </div>
              {/* 第二行：时间预设+范围、客户名称下拉、尾程商下拉 */}
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Select
                    value={timeRangePreset}
                    onChange={handleTimeRangePresetChange}
                    className="w-[120px]"
                    size="middle"
                    options={[
                      { value: '6h', label: t('wms.progress.filters.last6h') },
                      { value: '12h', label: t('wms.progress.filters.last12h') },
                      { value: '24h', label: t('wms.progress.filters.last24h') },
                      { value: '48h', label: t('wms.progress.filters.last48h') },
                      { value: 'custom', label: t('wms.progress.filters.custom') },
                    ]}
                  />
                  <RangePicker
                    value={timeRange && Array.isArray(timeRange) && timeRange.length === 2 ? [
                      timeRange[0] ? dayjs(timeRange[0]) : null,
                      timeRange[1] ? dayjs(timeRange[1]) : null
                    ] : null}
                    onChange={handleTimeRangePickerChange}
                    disabled={timeRangePreset !== 'custom'}
                    className="flex-1 w-0 min-w-0 max-w-full h-8"
                    format="YYYY/MM/DD HH"
                    showTime={{
                      format: 'HH',
                      defaultValue: [dayjs('00', 'HH'), dayjs('23', 'HH')],
                    }}
                    size="middle"
                    allowClear
                  />
                </div>
              </div>
              <div className="min-w-0">
                <Select
                  options={channelOptions}
                  value={channelId || undefined}
                  onChange={(value: string | undefined) => handleChannelChange(value)}
                  className="w-full"
                  size="middle"
                  placeholder={t('components.input.all')}
                  allowClear
                  showSearch
                />
              </div>
              <div className="min-w-0">
                <Select
                  options={carrierOptions}
                  value={carrierCode?.toString() || undefined}
                  onChange={(value: string | undefined) => handleCarrierChange(value)}
                  className="w-full"
                  size="middle"
                  placeholder={t('components.input.all')}
                  allowClear
                  showSearch
                />
              </div>
            </div>
          ) : (
            // 中文布局：时间预设和时间范围在同一行
            <div className="grid gap-x-10 gap-y-4" style={{ gridTemplateColumns: '1.3fr 1fr 1fr' }}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Select
                    value={timeRangePreset}
                    onChange={handleTimeRangePresetChange}
                    className="w-[120px]"
                    size="middle"
                    options={[
                      { value: '6h', label: t('wms.progress.filters.last6h') },
                      { value: '12h', label: t('wms.progress.filters.last12h') },
                      { value: '24h', label: t('wms.progress.filters.last24h') },
                      { value: '48h', label: t('wms.progress.filters.last48h') },
                      { value: 'custom', label: t('wms.progress.filters.custom') },
                    ]}
                  />
                  <RangePicker
                    value={timeRange && Array.isArray(timeRange) && timeRange.length === 2 ? [
                      timeRange[0] ? dayjs(timeRange[0]) : null,
                      timeRange[1] ? dayjs(timeRange[1]) : null
                    ] : null}
                    onChange={handleTimeRangePickerChange}
                    disabled={timeRangePreset !== 'custom'}
                    className="flex-1 w-0 min-w-0 max-w-full h-8"
                    format="YYYY/MM/DD HH"
                    showTime={{
                      format: 'HH',
                      defaultValue: [dayjs('00', 'HH'), dayjs('23', 'HH')],
                    }}
                    size="middle"
                    allowClear
                  />
                </div>
              </div>
              <div className="min-w-0">
                <ParcelFilterStyle
                  label={t('wms.progress.filters.clientName')}
                  options={channelOptions}
                  value={channelId || undefined}
                  onChange={(value: string | undefined) => handleChannelChange(value)}
                  className="w-full"
                  spanClassName={colClientSpanClassName}
                />
              </div>
              <div className="min-w-0">
                <ParcelFilterStyle
                  label={t('wms.progress.filters.carrier')}
                  options={carrierOptions}
                  value={carrierCode?.toString() || undefined}
                  onChange={(value: string | undefined) => handleCarrierChange(value)}
                  className="w-full"
                  spanClassName={colCarrierSpanClassName}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {/* 按钮行 - 放在一行 */}
      {(onFilter || onReset) && (
        <div className="flex items-center gap-2 mt-6 mb-3">
          {onFilter && (
            <Button 
              type="primary" 
              onClick={onFilter}
              className="w-auto min-w-24"
            >
              <span className="font-bold">{t('wms.exceptions.filters.filter')}</span>
            </Button>
          )}
          {onReset && (
            <Button 
              onClick={handleReset}
              className="w-auto min-w-24"
            >
              <span className="font-bold">{t('wms.exceptions.filters.reset')}</span>
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkProgressFilters;
